import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
} from "@tanstack/react-query";
import { ApiClientError, requestApiJson } from "../../api-client";
import {
  BrowseResponseSchema,
  ConversationListSchema,
  ConversationSchema,
  MyMarketplaceSchema,
  type Filters,
  marketplacePriceToMinor,
  type MarketplaceConversation,
} from "./marketplace-contracts";
import {
  SAMPLE_MARKETPLACE_LISTINGS,
  type MarketplacePreviewListing,
} from "./marketplace-sample-data";

export const marketplaceQueryKeys = {
  browse: ["marketplace", "browse"] as const,
  browsePage: (filters: Filters) => ["marketplace", "browse", filters] as const,
  mine: ["marketplace", "mine"] as const,
  conversations: ["marketplace", "conversations"] as const,
  conversation: (id: string) => ["marketplace", "conversation", id] as const,
};

interface MarketplaceBrowsePage {
  readonly mode: "live" | "sample";
  readonly listings: readonly MarketplacePreviewListing[];
  readonly nextCursor: string | null;
}

export type MarketplaceBrowseData = InfiniteData<
  MarketplaceBrowsePage,
  string | null
>;
export type MarketplaceConversationData = InfiniteData<
  MarketplaceConversation,
  unknown
>;

const MARKETPLACE_PAGE_SIZE = 24;

export function marketplaceBrowseQueryOptions(filters: Filters) {
  return infiniteQueryOptions({
    queryKey: marketplaceQueryKeys.browsePage(filters),
    queryFn: async ({ pageParam }) => {
      try {
        const response = await requestApiJson(
          marketplaceBrowseUrl(filters, pageParam),
          BrowseResponseSchema,
        );
        if (
          pageParam === null &&
          process.env.NODE_ENV !== "production" &&
          response.listings.length === 0
        ) {
          return {
            mode: "sample" as const,
            listings: SAMPLE_MARKETPLACE_LISTINGS,
            nextCursor: null,
          };
        }
        return { mode: "live" as const, ...response };
      } catch (error: unknown) {
        if (
          pageParam === null &&
          process.env.NODE_ENV !== "production" &&
          !(error instanceof ApiClientError)
        ) {
          return {
            mode: "sample" as const,
            listings: SAMPLE_MARKETPLACE_LISTINGS,
            nextCursor: null,
          };
        }
        throw error;
      }
    },
    initialPageParam: filters.cursor === "" ? null : filters.cursor,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

export function myMarketplaceQueryOptions({
  enabled = true,
}: { readonly enabled?: boolean } = {}) {
  return queryOptions({
    queryKey: marketplaceQueryKeys.mine,
    queryFn: () =>
      requestApiJson("/api/v1/marketplace/me", MyMarketplaceSchema),
    enabled,
    staleTime: 30_000,
  });
}

export function marketplaceConversationQueryOptions(id: string | null) {
  return infiniteQueryOptions({
    queryKey: marketplaceQueryKeys.conversation(id ?? "closed"),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (typeof pageParam === "string") params.set("cursor", pageParam);
      return requestApiJson(
        `/api/v1/marketplace/conversations/${id ?? "closed"}?${params.toString()}`,
        ConversationSchema,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: id !== null,
    staleTime: 30_000,
    refetchInterval: id === null ? false : 10_000,
  });
}

export function marketplaceConversationFromPages(
  data: MarketplaceConversationData | undefined,
): MarketplaceConversation | null {
  if (data === undefined) return null;
  const latest = data.pages[0];
  if (latest === undefined) return null;
  const seen = new Set<string>();
  const messages = [...data.pages]
    .reverse()
    .flatMap((page) => page.messages)
    .filter((message) => {
      if (seen.has(message.id)) return false;
      seen.add(message.id);
      return true;
    });
  return {
    ...latest,
    messages,
    nextCursor: data.pages.at(-1)?.nextCursor ?? null,
  };
}

export function initialMarketplaceConversationData(
  conversation: MarketplaceConversation,
): MarketplaceConversationData {
  return { pages: [conversation], pageParams: [null] };
}

export function marketplaceConversationsQueryOptions({
  enabled = true,
}: { readonly enabled?: boolean } = {}) {
  return infiniteQueryOptions({
    queryKey: marketplaceQueryKeys.conversations,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "20" });
      if (pageParam !== null) params.set("cursor", pageParam);
      return requestApiJson(
        `/api/v1/marketplace/conversations?${params.toString()}`,
        ConversationListSchema,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });
}

export function updateBrowseListing(
  data: MarketplaceBrowseData | undefined,
  listingId: string,
  update: (listing: MarketplacePreviewListing) => MarketplacePreviewListing,
) {
  if (data === undefined) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      listings: page.listings.map((listing) =>
        listing.id === listingId ? update(listing) : listing,
      ),
    })),
  };
}

function marketplaceBrowseUrl(
  filters: Filters,
  cursor: string | null,
): `/api/v1/${string}` {
  const params = new URLSearchParams({ limit: String(MARKETPLACE_PAGE_SIZE) });
  setQueryParam(params, "search", filters.search.trim());
  setQueryParam(params, "breed", filters.breed);
  setQueryParam(params, "sex", filters.sex);
  setQueryParam(params, "minimumAge", filters.minimumAge);
  setQueryParam(params, "maximumAge", filters.maximumAge);
  if (filters.minimumPrice !== "") {
    params.set(
      "minimumPriceMinor",
      marketplacePriceToMinor(filters.minimumPrice),
    );
  }
  if (filters.maximumPrice !== "") {
    params.set(
      "maximumPriceMinor",
      marketplacePriceToMinor(filters.maximumPrice),
    );
  }
  if (filters.minimumPrice !== "" || filters.maximumPrice !== "") {
    params.set("currency", filters.currency);
  }
  setQueryParam(params, "location", filters.location.trim());
  if (filters.verifiedOnly) params.set("verifiedOwner", "true");
  if (filters.sort !== "NEWEST") params.set("sort", filters.sort);
  if (cursor !== null) params.set("cursor", cursor);
  return `/api/v1/marketplace/listings?${params.toString()}`;
}

function setQueryParam(
  params: URLSearchParams,
  name: string,
  value: string,
): void {
  if (value !== "") params.set(name, value);
}
